import { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  MapPin,
  Video,
  Users,
  X,
  ExternalLink,
  BookOpen,
  AlertCircle
} from 'lucide-react';

// dev logger – silenced in production builds
const devLog = (...args) => {
  if (import.meta.env.MODE !== 'production') console.log(...args);
};
import useAuthStore from '../../context/authStore';
import useClientServices from '../../hooks/useClientServices';
import { isClientWorkerRole } from '../../utils/roles';

const eventTypeColors = {
  discovery_call: 'bg-blue-100 text-blue-700 border-blue-300',
  consultation: 'bg-green-100 text-green-700 border-green-300',
  project_kickoff: 'bg-purple-100 text-purple-700 border-purple-300',
  check_in: 'bg-amber-100 text-amber-700 border-amber-300',
  review: 'bg-cyan-100 text-cyan-700 border-cyan-300',
  presentation: 'bg-pink-100 text-pink-700 border-pink-300',
  internal: 'bg-gray-100 text-gray-700 border-gray-300',
  other: 'bg-gray-100 text-gray-700 border-gray-300',
  staffing_assignment: 'bg-teal-100 text-teal-700 border-teal-300',
  enrollment_start: 'bg-blue-100 text-blue-700 border-blue-300',
  enrollment_due: 'bg-amber-100 text-amber-700 border-amber-300',
  enrollment_complete: 'bg-green-100 text-green-700 border-green-300',
  staffing_alert_unfilled: 'bg-red-100 text-red-700 border-red-300',
};

const eventTypeLabels = {
  discovery_call: 'Discovery Call',
  consultation: 'Consultation',
  project_kickoff: 'Project Kickoff',
  check_in: 'Check-in',
  review: 'Review',
  presentation: 'Presentation',
  internal: 'Internal',
  other: 'Other',
  staffing_assignment: 'Staffing Assignment',
  staffing_alert_unfilled: 'Unfilled Staffing Alert',
  enrollment_start: 'Training Assigned',
  enrollment_due: 'Training Due',
  enrollment_complete: 'Training Completed',
};

const genericEventTypes = {
  consultation: 'Consultation',
  project_kickoff: 'Project Kickoff',
  check_in: 'Check-in',
  review: 'Review',
  presentation: 'Presentation',
  internal: 'Internal',
  other: 'Other',
};

const consultingEventTypes = {
  discovery_call: 'Discovery Call',
  ...genericEventTypes,
};

export default function CalendarPage() {
  const { token, user, isInternalTeam } = useAuthStore();
  const { services, loading: servicesLoading, error: servicesError } = useClientServices();

  devLog('CalendarPage - Services:', { services, servicesLoading, servicesError });

  // Determine calendar access based on role and services
  const isConsultingAvailable = services?.consulting?.enabled ?? false;
  const isLmsAvailable = services?.lms?.enabled ?? false;
  const isStaffingAvailable = services?.staffing?.enabled ?? false;
  const isClientWorker = isClientWorkerRole(user);
  const isOrganizationAdmin = user?.role === 'org_admin';
  const isClientAdmin = user?.role === 'facility_admin';
  const isClientManager = user?.role === 'facility_manager';
  const canScheduleMeetings = isOrganizationAdmin || isClientAdmin || isClientManager;
  const showStaffingTab = (isClientAdmin || isClientManager) && isStaffingAvailable;

  const shouldRedirectToAdmin = isInternalTeam() && token;
  const shouldBlockWorkerCalendar = isClientWorker && !isLmsAvailable && !isConsultingAvailable && !isStaffingAvailable;
  const hasAnyServiceEnabled = isConsultingAvailable || isLmsAvailable || isStaffingAvailable;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week');
  // facility_worker should default to training view; they will only see consulting meetings
  // if explicitly invited (and we show the tab in that case)
  const [activeTab, setActiveTab] = useState(() => {
    return isClientWorker ? 'training' : 'consulting';
  });
  const [events, setEvents] = useState([]);
  const [trainingEvents, setTrainingEvents] = useState([]);
  const [staffingEvents, setStaffingEvents] = useState([]);
  const [hasConsultingEvents, setHasConsultingEvents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventModalContext, setEventModalContext] = useState('generic'); // 'generic' or 'consulting'

  // Reschedule UI state
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [rescheduleTimes, setRescheduleTimes] = useState({ startTime: '', endTime: '' });
  // due-date change request state
  const [requestMode, setRequestMode] = useState(false);
  const [requestDate, setRequestDate] = useState('');
  const [requestComment, setRequestComment] = useState('');
  
  const [attendeeEmail, setAttendeeEmail] = useState('');
  
  // New event form state
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    type: 'internal',
    meetingUrl: '',
    attendees: [],
  });

  // Redirect internal team members to admin after hooks initialize (avoid hook-order changes).
  useEffect(() => {
    if (!shouldRedirectToAdmin) return;
    const adminUrl = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';
    if (typeof window !== 'undefined') {
      window.location.assign(adminUrl);
    }
  }, [shouldRedirectToAdmin]);

  useEffect(() => {
    fetchEvents();
  }, [currentDate, view, activeTab, isLmsAvailable, isConsultingAvailable, isStaffingAvailable]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      // Always fetch internal calendar events (meetings between team members)
      const start = getViewStart();
      const end = getViewEnd();
      
      // Fetch all events (internal + consulting)
      const res = await fetch(
        `/api/calendar/events?start=${start.toISOString()}&end=${end.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        throw new Error(`Failed to load calendar events (${res.status})`);
      }
      const data = await res.json();
      const evts = data.events || [];
      setEvents(evts);
      // determine if there are any non-training (consulting/internal) events for client workers
      if (isClientWorker) {
        setHasConsultingEvents(evts.some(e => e.type !== 'training'));
      }

      // Fetch training calendar events (if available and viewing training tab)
      if ((activeTab === 'training' || !canScheduleMeetings) && isLmsAvailable) {
        const start = getViewStart();
        const end = getViewEnd();
        const res = await fetch(
          `/api/lms/calendar/events?start=${start.toISOString()}&end=${end.toISOString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          const evts = (data.events || []).map(e => ({
            ...e,
            userId: e.userId || user?._id, // fallback to current user if server omitted it
          }));
          setTrainingEvents(evts);
        } else {
          // Do not fail the whole calendar for training endpoint errors.
          setTrainingEvents([]);
        }
      }

      // Fetch staffing calendar events (if available)
      if ((activeTab === 'staffing' || showStaffingTab) && isStaffingAvailable) {
        const start = getViewStart();
        const end = getViewEnd();
        const res = await fetch(
          `/api/staffing-calendar/assignments?start=${start.toISOString()}&end=${end.toISOString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          setStaffingEvents([]);
          return;
        }
        const data = await res.json();
        const assignmentEvents = (data.assignments || []).map((event) => ({
          ...event,
          title:
            event.title ||
            `${event.workerName || 'Worker'} - ${event.position || 'Staffing assignment'}`,
        }));
        const alertEvents = (data.alerts || []).map((event) => ({
          ...event,
          title: event.title || `${event.position || 'Request'} unfilled`,
        }));
        setStaffingEvents([...assignmentEvents, ...alertEvents]);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setLoading(false);
    }
  };

  const getViewStart = () => {
    const d = new Date(currentDate);
    if (view === 'week') d.setDate(d.getDate() - d.getDay());
    else d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getViewEnd = () => {
    const d = new Date(currentDate);
    if (view === 'week') d.setDate(d.getDate() + (6 - d.getDay()));
    else d.setMonth(d.getMonth() + 1, 0);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const navigatePrev = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - (view === 'week' ? 7 : 30));
    setCurrentDate(d);
  };

  const navigateNext = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + (view === 'week' ? 7 : 30));
    setCurrentDate(d);
  };

  const getWeekDays = () => {
    const start = getViewStart();
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  const getEventsForDay = (date) => {
    const dayEvents = events.filter(e => 
      new Date(e.startTime).toDateString() === date.toDateString()
    );
    // Filter by active tab
    if (activeTab === 'training') return trainingEvents.filter(e => new Date(e.startTime || e.dueDate).toDateString() === date.toDateString());
    if (activeTab === 'staffing') return staffingEvents.filter(e => new Date(e.startTime).toDateString() === date.toDateString());
    return dayEvents;
  };

  const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
  const formatLocation = (location) => {
    if (!location) return '';
    if (typeof location === 'string') return location;
    const parts = [location.facility, location.address, location.city, location.state].filter(Boolean);
    return parts.join(', ');
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();

  // Event creation is handled in the admin portal. Clients cannot create events directly via this UI.

  const addAttendee = () => {
    if (attendeeEmail && !newEvent.attendees.find(a => a.email === attendeeEmail)) {
      setNewEvent({ ...newEvent, attendees: [...newEvent.attendees, { email: attendeeEmail, name: '' }] });
      setAttendeeEmail('');
    }
  };

  // dev: log selectedEvent for debugging request button
  useEffect(() => {
    if (selectedEvent) {
      console.log('calendar selectedEvent', selectedEvent, 'current user', user);
    }
  }, [selectedEvent, user]);

  if (shouldRedirectToAdmin) {
    return null;
  }

  if (shouldBlockWorkerCalendar) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="card p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-secondary-400" />
          <h2 className="text-lg font-semibold text-secondary-900 mb-2">Calendar Not Available</h2>
          <p className="text-secondary-600">Your organization does not have the required services enabled for calendar functionality.</p>
        </div>
      </div>
    );
  }

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/calendar/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newEvent.title,
          description: newEvent.description,
          startTime: new Date(newEvent.startTime).toISOString(),
          endTime: new Date(newEvent.endTime).toISOString(),
          type: 'internal',
          attendees: newEvent.attendees,
          meetingUrl: newEvent.meetingUrl || undefined,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to create meeting');
      }

      // Clear form and close modal
      setNewEvent({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        type: 'internal',
        meetingUrl: '',
        attendees: [],
      });
      setAttendeeEmail('');
      setEventModalContext('generic');
      setShowEventModal(false);

      // Refresh events
      fetchEvents();
    } catch (err) {
      console.error('Error creating event:', err);
      alert('Failed to create meeting. Please try again.');
    }
  };

  return (
    <div className="p-0 sm:p-2 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Calendar</h1>
          <p className="text-gray-600 mt-1">
            {activeTab === 'consulting' ? 'Manage meetings and appointments' : 'Your training schedule and deadlines'}
          </p>
        </div>
        {canScheduleMeetings && (
          <button
            onClick={() => { setEventModalContext('generic'); setShowEventModal(true); }}
            className="flex w-full items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors sm:w-auto"
          >
            <Plus size={18} />
            Schedule Meeting
          </button>
        )}
      </div>

      {/* Show message if no services enabled */}
      {!hasAnyServiceEnabled && !isClientWorker && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-800">
            No calendar services are currently enabled for your organization. Contact your administrator to enable Consulting, LMS, or Staffing services.
          </p>
        </div>
      )}

      {/* Tabs - Show based on available services and user role */}
      {/* hide entire tab bar for facility_worker unless they have consulting events */}
      {((isConsultingAvailable && (!isClientWorker || hasConsultingEvents)) || isLmsAvailable || showStaffingTab) && (
        <div className="grid grid-cols-1 gap-1 mb-6 border-b sm:flex sm:gap-2">
          {isConsultingAvailable && (!isClientWorker || hasConsultingEvents) && (
            <button
              onClick={() => setActiveTab('consulting')}
              className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'consulting' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Meetings
            </button>
          )}
          {isLmsAvailable && (
            <button
              onClick={() => setActiveTab('training')}
              className={`px-4 py-2 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'training' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <BookOpen size={16} />
              Training Schedule
            </button>
          )}
          {showStaffingTab && (
            <button
              onClick={() => setActiveTab('staffing')}
              className={`px-4 py-2 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'staffing' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <Users size={16} />
              Temp Staff Assignments
            </button>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="bg-white border rounded-xl p-4 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex w-full flex-wrap items-center justify-center gap-2 sm:w-auto sm:justify-start">
            <button onClick={navigatePrev} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft size={20} /></button>
            <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1 text-sm border rounded-lg hover:bg-gray-50">Today</button>
            <button onClick={navigateNext} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronRight size={20} /></button>
            <span className="font-semibold text-gray-900 ml-2">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
          </div>
          <div className="flex border rounded-lg overflow-hidden">
            <button onClick={() => setView('week')} className={`px-4 py-2 text-sm ${view === 'week' ? 'bg-green-600 text-white' : 'hover:bg-gray-50'}`}>Week</button>
            <button onClick={() => setView('month')} className={`px-4 py-2 text-sm ${view === 'month' ? 'bg-green-600 text-white' : 'hover:bg-gray-50'}`}>Month</button>
          </div>
        </div>
      </div>
      {/* Generic Calendar View - Always show */}
      <>
        {/* Week View */}
        {view === 'week' && (
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="divide-y sm:hidden">
              {getWeekDays().map((day, idx) => {
                const dayEvents = getEventsForDay(day);
                return (
                  <section key={idx} className={isToday(day) ? 'bg-green-50/50' : ''}>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-xs font-medium uppercase text-gray-500">{day.toLocaleDateString('en-US', { weekday: 'long' })}</p>
                        <p className={`font-semibold ${isToday(day) ? 'text-green-600' : 'text-gray-900'}`}>{day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                      </div>
                      <span className="text-xs text-gray-500">{dayEvents.length} {dayEvents.length === 1 ? 'event' : 'events'}</span>
                    </div>
                    {dayEvents.length > 0 ? <div className="space-y-2 px-4 pb-4">{dayEvents.map((event) => (
                      <button key={event._id} onClick={() => setSelectedEvent(event)} className={`w-full rounded-lg border p-3 text-left text-sm ${eventTypeColors[event.type] || eventTypeColors.other}`}>
                        <p className="font-medium">{event.title}</p>
                        <p className="mt-1 text-xs text-gray-600">{formatTime(event.startTime)}</p>
                      </button>
                    ))}</div> : null}
                  </section>
                );
              })}
            </div>
            <div className="hidden grid-cols-7 border-b sm:grid">
              {getWeekDays().map((day, idx) => (
                <div key={idx} className={`p-3 text-center border-r last:border-r-0 ${isToday(day) ? 'bg-green-50' : ''}`}>
                  <div className="text-xs text-gray-500 uppercase">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                  <div className={`text-lg font-semibold ${isToday(day) ? 'text-green-600' : 'text-gray-900'}`}>{day.getDate()}</div>
                </div>
              ))}
            </div>
            <div className="hidden grid-cols-7 min-h-[400px] sm:grid">
              {getWeekDays().map((day, idx) => (
                <div key={idx} className={`p-2 border-r last:border-r-0 ${isToday(day) ? 'bg-green-50/50' : ''}`}>
                  {getEventsForDay(day).map((event) => (
                    <button key={event._id} onClick={() => setSelectedEvent(event)}
                      className={`w-full text-left p-2 mb-1 rounded text-xs border ${eventTypeColors[event.type] || eventTypeColors.other} hover:shadow-md transition-shadow`}>
                      <div className="font-medium truncate">{event.title}</div>
                      <div className="text-gray-600">{formatTime(event.startTime)}</div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Month View */}
        {view === 'month' && (
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="divide-y sm:hidden">
              {events.length === 0 ? <p className="p-8 text-center text-sm text-gray-500">No events this month.</p> : events.slice().sort((a, b) => new Date(a.startTime) - new Date(b.startTime)).map((event) => (
                <button key={event._id} onClick={() => setSelectedEvent(event)} className="flex w-full items-start gap-3 p-4 text-left hover:bg-gray-50">
                  <div className="w-12 shrink-0 rounded-lg border bg-gray-50 py-2 text-center">
                    <p className="text-xs uppercase text-gray-500">{new Date(event.startTime).toLocaleDateString('en-US', { month: 'short' })}</p>
                    <p className="text-lg font-semibold text-gray-900">{new Date(event.startTime).getDate()}</p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">{event.title}</p>
                    <p className="mt-1 text-sm text-gray-500">{formatTime(event.startTime)}</p>
                    <span className={`mt-2 inline-flex rounded border px-2 py-0.5 text-xs ${eventTypeColors[event.type] || eventTypeColors.other}`}>{eventTypeLabels[event.type] || 'Other'}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="hidden grid-cols-7 border-b sm:grid">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="p-3 text-center text-xs text-gray-500 uppercase font-medium">{day}</div>
              ))}
            </div>
            <div className="hidden grid-cols-7 sm:grid">
              {(() => {
                const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
                const days = [];
                for (let i = 0; i < firstDay.getDay(); i++) {
                  days.push(<div key={`pad-${i}`} className="p-2 border-b border-r bg-gray-50 min-h-[80px]" />);
                }
                for (let d = 1; d <= lastDay.getDate(); d++) {
                  const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), d);
                  const dayEvents = getEventsForDay(date);
                  days.push(
                    <div key={d} className={`p-2 border-b border-r min-h-[80px] ${isToday(date) ? 'bg-green-50' : ''}`}>
                      <div className={`text-sm font-medium mb-1 ${isToday(date) ? 'text-green-600' : 'text-gray-900'}`}>{d}</div>
                      {dayEvents.slice(0, 2).map((event) => (
                        <button key={event._id} onClick={() => setSelectedEvent(event)}
                          className={`w-full text-left p-1 mb-1 rounded text-xs truncate ${eventTypeColors[event.type] || eventTypeColors.other}`}>
                          {event.title}
                        </button>
                      ))}
                      {dayEvents.length > 2 && <div className="text-xs text-gray-500">+{dayEvents.length - 2} more</div>}
                    </div>
                  );
                }
                return days;
              })()}
            </div>
          </div>
        )}

        {/* Event Detail Modal */}
        {selectedEvent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  {selectedEvent.pendingDueDateRequest && (
                    <div className="mb-2 p-2 bg-yellow-50 border-l-4 border-yellow-400 text-sm text-yellow-800">
                      ⚠️ Pending deadline change request
                    </div>
                  )}
                  {selectedEvent.type && eventTypeColors[selectedEvent.type] && (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${eventTypeColors[selectedEvent.type]}`}>
                      {eventTypeLabels[selectedEvent.type]}
                    </span>
                  )}
                  <h2 className="text-xl font-bold text-gray-900 mt-2">{selectedEvent.title}</h2>
                </div>
                <button onClick={() => setSelectedEvent(null)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-600">
                  <Clock size={18} />
                  <div>
                    <div>{new Date(selectedEvent.startTime).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>
                    <div>{formatTime(selectedEvent.startTime)} - {formatTime(selectedEvent.endTime)}</div>
                  </div>
                </div>
                {selectedEvent.location && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <MapPin size={18} />
                    <span>{formatLocation(selectedEvent.location)}</span>
                  </div>
                )}
                {selectedEvent.meetingUrl && (
                  <div className="flex items-center gap-3">
                    <Video size={18} className="text-gray-600" />
                    <a href={selectedEvent.meetingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                      Join Meeting <ExternalLink size={14} />
                    </a>
                  </div>
                )}
                {selectedEvent.attendees?.length > 0 && (
                  <div className="flex items-start gap-3">
                    <Users size={18} className="text-gray-600 mt-0.5" />
                    <div className="flex flex-wrap gap-1">
                      {selectedEvent.attendees.map((a, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-100 rounded text-sm">{a.name || a.email}</span>
                      ))}
                    </div>
                  </div>
                )}
                {selectedEvent.description && <div className="pt-3 border-t"><p className="text-gray-600 text-sm">{selectedEvent.description}</p></div>}

                {/* Due-date change request for learner (only show on the due-date event) */}
                {selectedEvent && selectedEvent.type === 'enrollment_due' && !requestMode && (
                  <div className="pt-4 border-t">
                    <button onClick={() => {
                      setRequestMode(true);
                      // default to existing dueDate or enrolledAt if missing
                      const base = selectedEvent.dueDate || selectedEvent.startTime;
                      setRequestDate(new Date(base).toISOString().slice(0,16));
                    }} className="px-3 py-2 border rounded-lg">Request new due date</button>
                  </div>
                )}

                {requestMode && selectedEvent && selectedEvent.type === 'enrollment_due' && (
                  <div className="pt-4 border-t">
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 gap-2">
                        <input type="datetime-local" value={requestDate} onChange={(e) => setRequestDate(e.target.value)} className="w-full border rounded px-2 py-1" />
                        <textarea placeholder="Optional comment" value={requestComment} onChange={e => setRequestComment(e.target.value)} className="w-full border rounded px-2 py-1" rows={3} />
                      </div>
                      <div className="flex gap-2">
                        <button disabled={submitting} onClick={async () => {
                          // debug info
                          console.log('submitting due-date request', {
                            enrollmentId: selectedEvent?.enrollmentId,
                            requestDate,
                            requestComment,
                            token,
                          });
                          if (!selectedEvent?.enrollmentId) {
                            toast.error('Enrollment ID missing');
                            return;
                          }
                          setSubmitting(true);
                          if (!requestDate) {
                            toast.error('Please select a new due date');
                            setSubmitting(false);
                            return;
                          }
                          try {
                            const payload = { newDueDate: requestDate, comment: requestComment };
                            console.log('payload->', payload);
                            const res = await fetch(`/api/lms/enrollments/${selectedEvent.enrollmentId}/due-date-request`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify(payload),
                            });
                            const body = await res.json().catch(() => ({}));
                            console.log('due-date request response', res.status, body);
                            if (res.ok) {
                              toast.success(
                                body?.governsPathDeadline
                                  ? 'Path deadline extension request sent to administrator'
                                  : 'Request sent to administrator'
                              );
                              setRequestMode(false);
                              setSelectedEvent(prev => prev ? { ...prev, pendingDueDateRequest: true } : prev);
                              fetchEvents();
                            } else {
                              toast.error(`Failed to send request (${res.status})`);
                              // show any server error message
                              if (body && body.error) toast.error(body.error);
                              console.error('Failed to send request', body);
                            }
                          } catch (err) {
                            console.error('due-date request error', err);
                            toast.error('Network error sending request');
                          } finally {
                            setSubmitting(false);
                          }
                        }} className="px-3 py-2 bg-green-600 text-white rounded-lg">
                          {submitting ? 'Sending...' : 'Submit'}
                        </button>
                        <button onClick={() => setRequestMode(false)} className="px-3 py-2 border rounded-lg">Cancel</button>
                      </div>
                    </div>
                  </div>
                )}

                {(selectedEvent &&
                  selectedEvent.type && !selectedEvent.type.startsWith('enrollment_') &&
                  (selectedEvent.organizer?._id === user?._id || selectedEvent.attendees?.some(a => a.userId === user._id || a.email === user.email) || ['admin', 'stratel_admin'].includes(user.role))) && (
                  <div className="pt-4 border-t">
                    {!rescheduleMode && !requestMode ? (
                      <button onClick={() => { setRescheduleMode(true); setRescheduleTimes({ startTime: new Date(selectedEvent.startTime).toISOString().slice(0,16), endTime: new Date(selectedEvent.endTime).toISOString().slice(0,16) }); }} className="px-3 py-2 border rounded-lg">Reschedule</button>
                    ) : (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input type="datetime-local" value={rescheduleTimes.startTime} onChange={(e) => setRescheduleTimes({ ...rescheduleTimes, startTime: e.target.value })} className="w-full border rounded px-2 py-1" />
                          <input type="datetime-local" value={rescheduleTimes.endTime} onChange={(e) => setRescheduleTimes({ ...rescheduleTimes, endTime: e.target.value })} className="w-full border rounded px-2 py-1" />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={async () => {
                            try {
                              const res = await fetch(`/api/calendar/events/${selectedEvent._id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ startTime: rescheduleTimes.startTime, endTime: rescheduleTimes.endTime }) });
                              if (res.ok) {
                                setRescheduleMode(false);
                                setSelectedEvent(null);
                                fetchEvents();
                              } else {
                                console.error('Failed to reschedule');
                              }
                            } catch (err) { console.error(err); }
                          }} className="px-3 py-2 bg-green-600 text-white rounded-lg">Submit</button>
                          <button onClick={() => setRescheduleMode(false)} className="px-3 py-2 border rounded-lg">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
        )}

        {/* New Event Modal */}
        {showEventModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">New Event</h2>
                  <button onClick={() => { setEventModalContext('generic'); setShowEventModal(false); }} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                </div>
                <form onSubmit={createEvent} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input type="text" required value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2" placeholder="Meeting title" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
                    <input type="datetime-local" required value={newEvent.startTime} onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End *</label>
                    <input type="datetime-local" required value={newEvent.endTime} onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={newEvent.type} onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })} className="w-full border rounded-lg px-3 py-2">
                    {Object.entries(eventModalContext === 'consulting' ? consultingEventTypes : genericEventTypes).map(([value, label]) => (<option key={value} value={value}>{label}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting URL</label>
                  <input type="url" value={newEvent.meetingUrl} onChange={(e) => setNewEvent({ ...newEvent, meetingUrl: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2" placeholder="https://zoom.us/j/..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Attendees</label>
                  <div className="flex gap-2 mb-2">
                    <input type="email" value={attendeeEmail} onChange={(e) => setAttendeeEmail(e.target.value)}
                      className="flex-1 border rounded-lg px-3 py-2" placeholder="email@example.com" />
                    <button type="button" onClick={addAttendee} className="px-3 py-2 border rounded-lg hover:bg-gray-50">Add</button>
                  </div>
                  {newEvent.attendees.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {newEvent.attendees.map((a) => (
                        <span key={a.email} className="px-2 py-1 bg-gray-100 rounded text-sm flex items-center gap-1">
                          {a.email}
                          <button type="button" onClick={() => setNewEvent({ ...newEvent, attendees: newEvent.attendees.filter(x => x.email !== a.email) })}
                            className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Create Event</button>
                  <button type="button" onClick={() => { setEventModalContext('generic'); setShowEventModal(false); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      </>
    </div>
  );
}
